import { Component } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';

// ==============================|| ERROR BOUNDARY ||============================== //
// Catches any render error that would otherwise unmount the whole app and leave a blank white
// page (the app has no other error boundary anywhere). Two cases:
//  1. A stale JS chunk reference (react-lazy failing to fetch a route's bundle) — this happens
//     whenever the app is redeployed while a user already has the previous build open in their
//     tab, since the old index.html points at chunk filenames that no longer exist on the server.
//     There's nothing to "fix" in the UI for this one, so it force-reloads once automatically.
//  2. Any other unexpected render error — shown with a manual reload button instead of a blank page.
const CHUNK_ERROR_RELOAD_KEY = 'chunk-error-reloaded-at';

const isChunkLoadError = (error) =>
  Boolean(
    error &&
      (error.name === 'ChunkLoadError' ||
        /Loading chunk [\d\w]+ failed/i.test(error.message || '') ||
        /Failed to fetch dynamically imported module/i.test(error.message || ''))
  );

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, isChunkError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, isChunkError: isChunkLoadError(error) };
  }

  componentDidCatch(error, info) {
    console.error('Unhandled render error caught by ErrorBoundary:', error, info);

    if (isChunkLoadError(error)) {
      // Reload at most once per stale-chunk incident — avoids an infinite reload loop if the
      // server is genuinely down rather than just serving a newer build.
      const lastReload = Number(sessionStorage.getItem(CHUNK_ERROR_RELOAD_KEY) || 0);
      if (Date.now() - lastReload > 10000) {
        sessionStorage.setItem(CHUNK_ERROR_RELOAD_KEY, String(Date.now()));
        window.location.reload();
      }
    }
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.state.isChunkError) {
      // Reloading already; render nothing rather than a flash of error content.
      return null;
    }

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', p: 3 }}>
        <Stack spacing={2} alignItems="center" sx={{ textAlign: 'center', maxWidth: 360 }}>
          <Typography variant="h5">Something went wrong</Typography>
          <Typography variant="body2" color="text.secondary">
            The page hit an unexpected error. Reloading usually fixes it.
          </Typography>
          <Button variant="contained" onClick={() => window.location.reload()}>
            Reload page
          </Button>
        </Stack>
      </Box>
    );
  }
}

export default ErrorBoundary;
